import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ClipboardList, Eye, Calculator, FileCheck, Shield, HardHat, ChevronDown, MessageCircle } from 'lucide-react';
const steps = [{
  id: '01',
  title: 'Evaluación de estado actual',
  description: 'Analizamos lo que ya tienes o iniciamos el levantamiento desde cero. Entendemos tu punto de partida para trazar la ruta correcta.',
  icon: ClipboardList
}, {
  id: '02',
  title: 'Plano · Diseño · Render 3D',
  description: 'Visualizas la ingeniería y la estética antes de construir. Plantas, alzados e imágenes fotorrealistas para que apruebes cada detalle.',
  icon: Eye
}, {
  id: '03',
  title: 'Cuentas claras',
  description: 'Desglosamos cantidades de obra concreta. Nada se estima «al ojo»; cada metro, cada material tiene un cálculo preciso.',
  icon: Calculator
}, {
  id: '04',
  title: 'Presupuesto acordado',
  description: 'Revisión conjunta para un valor final justo. Revisamos cada rubro contigo hasta llegar a un acuerdo transparente.',
  icon: FileCheck
}, {
  id: '05',
  title: 'Todo bajo contrato',
  description: 'Protección legal y seriedad total. Firmamos un contrato oficial de obra civil para respaldar tu inversión.',
  icon: Shield
}, {
  id: '06',
  title: 'Ejecución y entrega',
  description: 'Dirección de obra constante hasta la entrega final. Programación detallada e informes de avance para tu total tranquilidad.',
  icon: HardHat
}];
export function ProcessSection() {
  const [openStep, setOpenStep] = useState<string | null>('01');
  const whatsappFase = 'https://wa.me/573182434499?text=Hola%20Gerfaj%2C%20quiero%20compartir%20la%20fase%20actual%20de%20mi%20proyecto.';
  const toggleStep = (id: string) => {
    setOpenStep(prev => prev === id ? null : id);
  };
  return <section className="py-24 md:py-32 bg-gerfaj-dark-lighter relative" data-id="element-145">
      <div className="container mx-auto px-4 md:px-6 max-w-3xl" data-id="element-146">
        <div className="text-center mb-12 md:mb-16" data-id="element-147">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-3xl md:text-5xl font-montserrat font-bold text-white mb-4" data-id="element-148">
            Tu proyecto, paso a paso y{' '}
            <span className="text-gerfaj-orange" data-id="element-149">sin enredos</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-gray-400 max-w-2xl mx-auto text-base md:text-lg" data-id="element-150">
            No importa si tienes un plano o solo una idea, nos adaptamos a la
            fase actual de tu proyecto.
          </motion.p>
        </div>

        {/* Accordion Container */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="glass-panel rounded-2xl md:rounded-3xl overflow-hidden divide-y divide-white/5" data-id="element-151">
          {steps.map(step => {
          const isOpen = openStep === step.id;
          const Icon = step.icon;
          return <div key={step.id} data-id="element-152">
                <button onClick={() => toggleStep(step.id)} className="w-full flex items-center gap-4 p-5 md:p-6 text-left transition-colors duration-200 hover:bg-white/[0.03] min-h-[64px]" aria-expanded={isOpen} data-id="element-153">
                  <span className={`font-montserrat font-bold text-sm md:text-base flex-shrink-0 transition-colors duration-300 ${isOpen ? 'text-gerfaj-orange' : 'text-gray-500'}`} data-id="element-154">
                    {step.id}
                  </span>

                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${isOpen ? 'bg-gerfaj-orange/15 border border-gerfaj-orange/30 text-gerfaj-orange' : 'bg-gerfaj-blue/30 border border-gerfaj-blue/50 text-gray-400'}`} data-id="element-155">
                    <Icon className="w-5 h-5 md:w-6 md:h-6" data-id="element-156" />
                  </div>

                  <h3 className={`flex-1 font-montserrat font-bold text-base md:text-lg transition-colors duration-300 ${isOpen ? 'text-white' : 'text-[#9ca3af]'}`} data-id="element-157">
                    {step.title}
                  </h3>

                  <motion.div animate={{
                rotate: isOpen ? 180 : 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="flex-shrink-0" data-id="element-158">
                    <ChevronDown className={`w-5 h-5 transition-colors duration-300 ${isOpen ? 'text-gerfaj-orange' : 'text-gray-500'}`} data-id="element-159" />
                  </motion.div>
                </button>

                <AnimatePresence initial={false} data-id="element-160">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-161">
                      <div className="px-5 md:px-6 pb-6 pl-[4.5rem] md:pl-[5.5rem]" data-id="element-162">
                        <p className="text-white leading-relaxed text-base" data-id="element-163">
                          {step.description}
                        </p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </motion.div>

        {/* CTA Button below accordion */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="flex justify-center mt-10" data-id="element-164">
          <motion.a href={whatsappFase} target="_blank" rel="noopener noreferrer" whileHover={{
          scale: 1.04
        }} whileTap={{
          scale: 0.96
        }} className="inline-flex items-center justify-center gap-3 bg-[#FF6600] hover:bg-orange-600 text-white font-montserrat font-bold text-lg px-8 py-4 rounded-2xl cta-glow transition-colors duration-300 w-[90%] sm:w-auto min-h-[60px]" data-id="element-165">
            <MessageCircle className="w-6 h-6" data-id="element-166" />
            Compartir mi fase actual
          </motion.a>
        </motion.div>

        {/* Blueprint vs Render Visual Module */}
      </div>
    </section>;
}